'use client';

import { useMessageMarker } from '@/@admin/hooks/useMessageMarker';
import { useMessageMarkerMutations } from '@/@admin/hooks/useMessageMarkerMutations';
import { MessageMarkerStatus } from '@/@admin/types/entities';
import {
    MESSAGE_MARKER_STATUS_COLORS,
    MESSAGE_MARKER_STATUS_ICONS,
    MESSAGE_MARKER_STATUS_LABELS,
} from '@/@admin/types/message-markers';
import { MessageMarkerButtonProps } from '@/@admin/types/props';
import { ROLE_TYPES } from '@/@studio-ai';
import {
    Button,
    Chip,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownTrigger,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Textarea,
    Tooltip,
    useDisclosure,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useCallback, useMemo, useState } from 'react';

export function MessageMarkerButton({
    message,
    conversationId,
    onMarkerUpdated,
}: MessageMarkerButtonProps) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedStatus, setSelectedStatus] = useState<MessageMarkerStatus | null>(null);
    const [notes, setNotes] = useState('');

    // Fetch existing marker for this message
    const { data: existingMarker } = useMessageMarker({
        conversationId,
        messageId: message.id,
    });

    // Extract text content from message
    const getMessageText = useCallback((): string => {
        if (typeof message.content === 'string') return message.content;
        if (Array.isArray(message.content)) {
            return message.content
                .filter((c) => c.type === 'text' && c.text)
                .map((c) => c.text)
                .join('\n');
        }
        return '';
    }, [message.content]);

    const messageText = useMemo(() => getMessageText(), [getMessageText]);
    const userMessage = message.role === ROLE_TYPES.USER ? messageText : undefined;
    const aiResponse = message.role === ROLE_TYPES.ASSISTANT ? messageText : undefined;

    const handleSuccess = useCallback(() => {
        onMarkerUpdated?.();
        setNotes('');
        onClose();
    }, [onMarkerUpdated, onClose]);

    const { createMarker, updateMarker, deleteMarker, isLoading } = useMessageMarkerMutations({
        conversationId,
        messageId: message.id,
        userMessage,
        aiResponse,
        onSuccess: handleSuccess,
    });

    const handleStatusSelect = useCallback(
        (status: MessageMarkerStatus) => {
            setSelectedStatus(status);
            if (existingMarker) {
                // If updating existing marker, pre-fill notes
                setNotes(existingMarker.notes || '');
            }
            onOpen();
        },
        [existingMarker, onOpen],
    );

    const handleSave = useCallback(() => {
        if (!selectedStatus) return;

        if (existingMarker) {
            updateMarker(existingMarker.id, selectedStatus, notes.trim() || undefined);
        } else {
            createMarker(selectedStatus, notes.trim() || undefined);
        }
    }, [selectedStatus, existingMarker, notes, updateMarker, createMarker]);

    const handleDelete = useCallback(() => {
        if (existingMarker) {
            deleteMarker(existingMarker.id);
        }
    }, [existingMarker, deleteMarker]);

    const handleModalClose = useCallback(() => {
        onClose();
        setNotes('');
        setSelectedStatus(null);
    }, [onClose]);

    return (
        <div className="flex justify-start">
            {existingMarker ? (
                <div className="flex items-center gap-2">
                    <Dropdown>
                        <DropdownTrigger>
                            <Chip
                                size="sm"
                                variant="flat"
                                color={MESSAGE_MARKER_STATUS_COLORS[existingMarker.status]}
                                className="cursor-pointer hover:bg-default-200"
                                aria-label={MESSAGE_MARKER_STATUS_LABELS[existingMarker.status]}
                                role="button"
                                tabIndex={0}
                            >
                                <Tooltip
                                    content={MESSAGE_MARKER_STATUS_LABELS[existingMarker.status]}
                                >
                                    <Icon
                                        icon={MESSAGE_MARKER_STATUS_ICONS[existingMarker.status]}
                                        width={14}
                                    />
                                </Tooltip>
                            </Chip>
                        </DropdownTrigger>
                        <DropdownMenu aria-label="Marker actions">
                            <DropdownItem
                                key={MessageMarkerStatus.SIGNIFICANT}
                                startContent={
                                    <Icon
                                        icon={
                                            MESSAGE_MARKER_STATUS_ICONS[
                                                MessageMarkerStatus.SIGNIFICANT
                                            ]
                                        }
                                        width={16}
                                    />
                                }
                                onPress={() => handleStatusSelect(MessageMarkerStatus.SIGNIFICANT)}
                            >
                                Change to{' '}
                                {MESSAGE_MARKER_STATUS_LABELS[MessageMarkerStatus.SIGNIFICANT]}
                            </DropdownItem>
                            <DropdownItem
                                key={MessageMarkerStatus.CONCERN}
                                startContent={
                                    <Icon
                                        icon={
                                            MESSAGE_MARKER_STATUS_ICONS[MessageMarkerStatus.CONCERN]
                                        }
                                        width={16}
                                    />
                                }
                                onPress={() => handleStatusSelect(MessageMarkerStatus.CONCERN)}
                            >
                                Change to{' '}
                                {MESSAGE_MARKER_STATUS_LABELS[MessageMarkerStatus.CONCERN]}
                            </DropdownItem>
                            <DropdownItem
                                key={MessageMarkerStatus.TRAINING_EXAMPLE}
                                startContent={
                                    <Icon
                                        icon={
                                            MESSAGE_MARKER_STATUS_ICONS[
                                                MessageMarkerStatus.TRAINING_EXAMPLE
                                            ]
                                        }
                                        width={16}
                                    />
                                }
                                onPress={() =>
                                    handleStatusSelect(MessageMarkerStatus.TRAINING_EXAMPLE)
                                }
                            >
                                Change to{' '}
                                {MESSAGE_MARKER_STATUS_LABELS[MessageMarkerStatus.TRAINING_EXAMPLE]}
                            </DropdownItem>
                            <DropdownItem
                                key={MessageMarkerStatus.FOLLOW_UP_NEEDED}
                                startContent={
                                    <Icon
                                        icon={
                                            MESSAGE_MARKER_STATUS_ICONS[
                                                MessageMarkerStatus.FOLLOW_UP_NEEDED
                                            ]
                                        }
                                        width={16}
                                    />
                                }
                                onPress={() =>
                                    handleStatusSelect(MessageMarkerStatus.FOLLOW_UP_NEEDED)
                                }
                            >
                                Change to{' '}
                                {MESSAGE_MARKER_STATUS_LABELS[MessageMarkerStatus.FOLLOW_UP_NEEDED]}
                            </DropdownItem>
                            <DropdownItem
                                key="delete"
                                color="danger"
                                startContent={<Icon icon="lucide:trash-2" width={16} />}
                                onPress={handleDelete}
                            >
                                Remove Marker
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </div>
            ) : (
                <Dropdown>
                    <DropdownTrigger>
                        <Button
                            isIconOnly
                            size="sm"
                            radius="full"
                            variant="ghost"
                            color="default"
                            startContent={<Icon icon="lucide:bookmark-plus" width={16} />}
                            isLoading={isLoading}
                            className="border-none"
                            aria-label="Add marker"
                            role="button"
                            tabIndex={0}
                        />
                    </DropdownTrigger>
                    <DropdownMenu aria-label="Select marker status">
                        <DropdownItem
                            key={MessageMarkerStatus.SIGNIFICANT}
                            startContent={
                                <Icon
                                    icon={
                                        MESSAGE_MARKER_STATUS_ICONS[MessageMarkerStatus.SIGNIFICANT]
                                    }
                                    width={16}
                                />
                            }
                            onPress={() => handleStatusSelect(MessageMarkerStatus.SIGNIFICANT)}
                        >
                            {MESSAGE_MARKER_STATUS_LABELS[MessageMarkerStatus.SIGNIFICANT]}
                        </DropdownItem>
                        <DropdownItem
                            key={MessageMarkerStatus.VALUABLE_TO_CLIENT}
                            startContent={
                                <Icon
                                    icon={
                                        MESSAGE_MARKER_STATUS_ICONS[
                                            MessageMarkerStatus.VALUABLE_TO_CLIENT
                                        ]
                                    }
                                    width={16}
                                />
                            }
                            onPress={() =>
                                handleStatusSelect(MessageMarkerStatus.VALUABLE_TO_CLIENT)
                            }
                        >
                            {MESSAGE_MARKER_STATUS_LABELS[MessageMarkerStatus.VALUABLE_TO_CLIENT]}
                        </DropdownItem>
                        <DropdownItem
                            key={MessageMarkerStatus.CONCERN}
                            startContent={
                                <Icon
                                    icon={MESSAGE_MARKER_STATUS_ICONS[MessageMarkerStatus.CONCERN]}
                                    width={16}
                                />
                            }
                            onPress={() => handleStatusSelect(MessageMarkerStatus.CONCERN)}
                        >
                            {MESSAGE_MARKER_STATUS_LABELS[MessageMarkerStatus.CONCERN]}
                        </DropdownItem>
                        <DropdownItem
                            key={MessageMarkerStatus.TRAINING_EXAMPLE}
                            startContent={
                                <Icon
                                    icon={
                                        MESSAGE_MARKER_STATUS_ICONS[
                                            MessageMarkerStatus.TRAINING_EXAMPLE
                                        ]
                                    }
                                    width={16}
                                />
                            }
                            onPress={() => handleStatusSelect(MessageMarkerStatus.TRAINING_EXAMPLE)}
                        >
                            {MESSAGE_MARKER_STATUS_LABELS[MessageMarkerStatus.TRAINING_EXAMPLE]}
                        </DropdownItem>
                        <DropdownItem
                            key={MessageMarkerStatus.FOLLOW_UP_NEEDED}
                            startContent={
                                <Icon
                                    icon={
                                        MESSAGE_MARKER_STATUS_ICONS[
                                            MessageMarkerStatus.FOLLOW_UP_NEEDED
                                        ]
                                    }
                                    width={16}
                                />
                            }
                            onPress={() => handleStatusSelect(MessageMarkerStatus.FOLLOW_UP_NEEDED)}
                        >
                            {MESSAGE_MARKER_STATUS_LABELS[MessageMarkerStatus.FOLLOW_UP_NEEDED]}
                        </DropdownItem>
                    </DropdownMenu>
                </Dropdown>
            )}

            {/* Notes Modal */}
            <Modal isOpen={isOpen} onClose={handleModalClose} size="lg">
                <ModalContent>
                    <ModalHeader>
                        <div className="flex items-center gap-2">
                            {selectedStatus && (
                                <Icon
                                    icon={MESSAGE_MARKER_STATUS_ICONS[selectedStatus]}
                                    width={20}
                                />
                            )}
                            {selectedStatus && MESSAGE_MARKER_STATUS_LABELS[selectedStatus]}
                        </div>
                    </ModalHeader>
                    <ModalBody>
                        <Textarea
                            label="Notes (Optional)"
                            placeholder="Add any additional notes about this message..."
                            value={notes}
                            onValueChange={setNotes}
                            minRows={4}
                            maxRows={8}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="light" onPress={handleModalClose}>
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            onPress={handleSave}
                            isLoading={isLoading}
                            isDisabled={!selectedStatus}
                        >
                            Save Marker
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    );
}
